const baseUrl: string = process.env.REACT_APP_BACKEND_URL || "";

export interface DictionaryWord {
  user_marked_word_id: number;
  word_base_form: string;
  word_translation: string;
  grammar: string;
  example_sentence: string;
  example_sentence_translation: string;
  word_original_language: string;
  user_comment?: string | null;
  ease_factor: number;
  interval: number;
  lapses: number;
  success_streak: number;
  last_reviewed_at: string | null;
  next_review_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AddWordToDictionaryPayload {
  word_base_form: string;
  word_translation: string;
  grammar: string;
  example_sentence: string;
  example_sentence_translation: string;
  word_original_language: string;
}

/**
 * Fetch all words in the user's dictionary
 * @param language Optional language filter (e.g., 'en-US', 'es-ES')
 */
export const fetchDictionaryWords = async (language?: string): Promise<DictionaryWord[]> => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const url = language 
      ? `${baseUrl}/dictionary/?language=${language}`
      : `${baseUrl}/dictionary/`;

    console.log('[dictionaryApi] Fetching words from:', url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[dictionaryApi] Error response:', errorText);
      throw new Error("Failed to fetch dictionary words");
    }

    const data = await response.json();
    console.log('[dictionaryApi] Received data:', data);
    
    // Handle both paginated and non-paginated responses
    // Paginated: { count, next, previous, results: [...] }
    // Non-paginated: [...]
    if (data && typeof data === 'object' && 'results' in data) {
      // Paginated response
      return Array.isArray(data.results) ? data.results : [];
    }
    
    // Non-paginated response (direct array)
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching dictionary words:", error);
    throw error;
  }
};

/**
 * Get list of languages that have words in the user's dictionary
 */
export const fetchDictionaryLanguages = async (): Promise<string[]> => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const response = await fetch(`${baseUrl}/dictionary/languages/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch dictionary languages");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching dictionary languages:", error);
    throw error;
  }
};

/**
 * Add a word to the user's dictionary
 */
export const addWordToDictionary = async (
  payload: AddWordToDictionaryPayload
): Promise<DictionaryWord> => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const response = await fetch(`${baseUrl}/dictionary/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to add word to dictionary");
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding word to dictionary:", error);
    throw error;
  }
};

export interface UpdateDictionaryWordPayload {
  word_base_form?: string;
  word_translation?: string;
  grammar?: string;
  example_sentence?: string;
  example_sentence_translation?: string;
  word_original_language?: string;
  user_comment?: string | null;
}

/**
 * Update a word in the dictionary
 */
export const updateDictionaryWord = async (
  wordId: number,
  payload: UpdateDictionaryWordPayload
): Promise<DictionaryWord> => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const response = await fetch(`${baseUrl}/dictionary/${wordId}/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to update dictionary word");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating dictionary word:", error);
    throw error;
  }
};

/**
 * Delete a word from the dictionary
 */
export const deleteWordFromDictionary = async (wordId: number): Promise<void> => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const response = await fetch(`${baseUrl}/dictionary/${wordId}/`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to delete word from dictionary");
    }
  } catch (error) {
    console.error("Error deleting word from dictionary:", error);
    throw error;
  }
};

export interface ReviewQueueResponse {
  cards: DictionaryWord[];
  available_count: number;
}

/**
 * Fetch the next set of due flashcards, optionally filtered by language.
 */
export const fetchReviewQueue = async (
  language?: string,
  limit: number = 10
): Promise<ReviewQueueResponse> => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (language) {
      params.set("language", language);
    }
    if (limit) {
      params.set("limit", String(limit));
    }
    const query = params.toString();
    const url = `${baseUrl}/dictionary/review-queue/${query ? `?${query}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to fetch review queue");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to load review queue:", error);
    throw error;
  }
};

/**
 * Submit a spaced repetition review result.
 */
export const submitReviewResult = async (
  cardId: number,
  rating: number
): Promise<DictionaryWord> => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const response = await fetch(`${baseUrl}/dictionary/${cardId}/review/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ rating }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to submit review");
    }

    return await response.json();
  } catch (error) {
    console.error("Error submitting review:", error);
    throw error;
  }
};
